// Shared data helpers for the Blåtur pages
// Blåtur trips are regular documents in the 'events' collection with event_type 'Blåtur🌍'

import { useEffect, useState } from 'react';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { app } from './firebase-config';
import { tripYear, tripTitle, BLATUR_TYPE } from './tripHelpers';


const db = getFirestore(app);
const auth = getAuth(app);

export { tripYear, tripTitle, tripCover, BLATUR_TYPE } from './tripHelpers';

// Logged-in user (undefined while Firebase is still checking)
export function useAuthUser() {
  const [user, setUser] = useState(undefined);
  useEffect(() => onAuthStateChanged(auth, setUser), []);
  return user;
}

// All blåtur trips, newest first, plus lowercase name -> user id (profile links), user id -> name
// and lowercase name -> profile picture
export function useBlaturTrips(user) {
  const [trips, setTrips] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [userNames, setUserNames] = useState({});
  const [profileImages, setProfileImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(user === undefined);
      return;
    }
    const load = async () => {
      try {
        const [eventsSnap, usersSnap] = await Promise.all([
          getDocs(query(collection(db, 'events'), where('event_type', '==', BLATUR_TYPE))),
          getDocs(collection(db, 'users')),
        ]);
        const list = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.Kvartal || '').localeCompare(a.Kvartal || ''));
        setTrips(list);

        const map = {};
        const names = {};
        const images = {};
        usersSnap.docs.forEach(d => {
          const { name, profileImageUrl } = d.data();
          if (name) {
            map[name.toLowerCase()] = d.id;
            names[d.id] = name.trim();
            if (profileImageUrl) images[name.trim().toLowerCase()] = profileImageUrl;
          }
        });
        setUserMap(map);
        setUserNames(names);
        setProfileImages(images);
      } catch (error) {
        console.error('Error fetching blåtur trips: ', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  return { trips, userMap, userNames, profileImages, loading };
}

// One row per person, one column per trip (oldest first): 'host' | 'participant' | null
export function buildParticipationMatrix(trips) {
  const columns = [...trips].sort((a, b) => (a.Kvartal || '').localeCompare(b.Kvartal || ''));
  const people = {};

  columns.forEach((trip, i) => {
    const clean = (list) => (list || []).map(n => (n || '').trim()).filter(Boolean);
    const hosts = clean(trip.Vert);
    const names = new Set([...clean(trip.Deltagelse), ...hosts]);
    names.forEach(name => {
      if (!people[name]) people[name] = { name, cells: Array(columns.length).fill(null), total: 0, hosted: 0 };
      people[name].cells[i] = hosts.includes(name) ? 'host' : 'participant';
      people[name].total += 1;
      if (hosts.includes(name)) people[name].hosted += 1;
    });
  });

  const rows = Object.values(people).map(p => {
    const lastIndex = p.cells.map(c => !!c).lastIndexOf(true);
    return { ...p, lastYear: tripYear(columns[lastIndex]) };
  });
  rows.sort((a, b) => b.total - a.total || (b.lastYear || 0) - (a.lastYear || 0) || a.name.localeCompare(b.name));

  return { columns, rows };
}

// Excel workbook with a participation matrix and a trip list
export function exportBlaturToExcel(trips) {
  const { columns, rows } = buildParticipationMatrix(trips);
  const label = (trip) => `${tripYear(trip)} ${tripTitle(trip)}`;

  const matrix = rows.map(row => {
    const line = { Navn: row.name };
    columns.forEach((trip, i) => {
      const cell = row.cells[i];
      line[label(trip)] = cell === 'host' ? 'Arrangør' : cell === 'participant' ? 'Deltok' : '';
    });
    line['Antall turer'] = row.total;
    line['Arrangert'] = row.hosted;
    line['Sist med'] = row.lastYear;
    return line;
  });

  const tripRows = [...columns].reverse().map(trip => ({
    'År': tripYear(trip),
    'Destinasjon': tripTitle(trip),
    'Arrangører': (trip.Vert || []).join(', '),
    'Antall deltakere': (trip.Deltagelse || []).length,
    'Deltakere': (trip.Deltagelse || []).join(', '),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(matrix), 'Deltakelse');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tripRows), 'Turer');
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'blatur_deltakelse.xlsx');
}
